import { useCallback, useMemo } from 'react';
import { TFunction } from 'i18next';
import { format, parseISO } from 'date-fns';
import { stripFileExtension } from '@/utils/file-name';
import { parseEngineSourceType, SourceType } from '@/types/source';
import {
    MILLISECONDS_IN_MINUTE,
    MILLISECONDS_IN_HOUR,
    MILLISECONDS_IN_DAY,
    MILLISECONDS_TO_SECONDS,
    DAYS_IN_WEEK,
    DAYS_IN_MONTH,
} from '@/constants/time';
import {
    formatChartDateMinutely,
    formatChartDate3Hourly,
    formatChartDateDaily,
    formatChartDateMonthly,
} from '@/utils/date';

export type PeriodOption =
    | '1hour'
    | '3hours'
    | '12hours'
    | '1day'
    | '3days'
    | '7days'
    | '2weeks'
    | '1month';

export type GroupByOption =
    | '5minutely'
    | '15minutely'
    | 'hourly'
    | '3hourly'
    | 'daily'
    | 'monthly';

export interface PeriodConfig {
    milliseconds: number;
    groupBy: GroupByOption;
    xAxisInterval?: number;
    bottomMargin?: number;
}

export const PERIOD_CONFIG: Record<PeriodOption, PeriodConfig> = {
    '1hour': { milliseconds: MILLISECONDS_IN_HOUR, groupBy: '5minutely' },
    '3hours': { milliseconds: 3 * MILLISECONDS_IN_HOUR, groupBy: '15minutely' },
    '12hours': { milliseconds: 12 * MILLISECONDS_IN_HOUR, groupBy: 'hourly' },
    '1day': { milliseconds: MILLISECONDS_IN_DAY, groupBy: 'hourly' },
    '3days': {
        milliseconds: 3 * MILLISECONDS_IN_DAY,
        groupBy: '3hourly',
        bottomMargin: 20,
    },
    '7days': {
        milliseconds: DAYS_IN_WEEK * MILLISECONDS_IN_DAY,
        groupBy: 'daily',
    },
    '2weeks': {
        milliseconds: 2 * DAYS_IN_WEEK * MILLISECONDS_IN_DAY,
        groupBy: 'daily',
    },
    '1month': {
        milliseconds: DAYS_IN_MONTH * MILLISECONDS_IN_DAY,
        groupBy: 'daily',
    },
};

export const GROUP_BY_DURATION: Record<GroupByOption, number> = {
    '5minutely': 5 * MILLISECONDS_IN_MINUTE,
    '15minutely': 15 * MILLISECONDS_IN_MINUTE,
    hourly: MILLISECONDS_IN_HOUR,
    '3hourly': 3 * MILLISECONDS_IN_HOUR,
    daily: MILLISECONDS_IN_DAY,
    monthly: 30 * MILLISECONDS_IN_DAY,
};

export function getStartTime(
    milliseconds: number,
    groupBy: GroupByOption,
): number {
    const startMs = Date.now() - milliseconds;
    const bucketMs = GROUP_BY_DURATION[groupBy];
    const tzOffsetMs = new Date().getTimezoneOffset() * -60 * 1000;
    const alignedMs =
        Math.floor((startMs + tzOffsetMs) / bucketMs) * bucketMs - tzOffsetMs;
    return Math.floor(alignedMs / MILLISECONDS_TO_SECONDS);
}

export function formatChartAxisByGroupBy(
    value: string,
    groupBy: GroupByOption,
): string {
    switch (groupBy) {
        case '5minutely':
        case '15minutely':
        case 'hourly':
            return formatChartDateMinutely(value);
        case '3hourly':
            return formatChartDate3Hourly(value);
        case 'daily':
            return formatChartDateDaily(value);
        case 'monthly':
            return formatChartDateMonthly(value);
        default:
            return value;
    }
}

export function formatChartTooltipTitleByGroupBy(
    value: string,
    groupBy: GroupByOption,
): string {
    try {
        const start = parseISO(value);
        return format(
            start,
            groupBy === 'monthly' ? 'MMMM yyyy' : 'MMMM d, yyyy',
        );
    } catch {
        return value;
    }
}

const PERIOD_LABEL_KEY: Record<PeriodOption, string> = {
    '1hour': 'chart.period1Hour',
    '3hours': 'chart.period3Hours',
    '12hours': 'chart.period12Hours',
    '1day': 'chart.period1Day',
    '3days': 'chart.period3Days',
    '7days': 'chart.period7Days',
    '2weeks': 'chart.period2Weeks',
    '1month': 'chart.period1Month',
};

export const BASE_PERIODS: PeriodOption[] = [
    '1hour',
    '3hours',
    '12hours',
    '1day',
    '3days',
    '7days',
];

export const ANALYTICS_PERIODS: PeriodOption[] = [
    ...BASE_PERIODS,
    '2weeks',
    '1month',
];

export function usePeriodOptions(
    t: TFunction,
    periods: PeriodOption[] = BASE_PERIODS,
) {
    return useMemo(
        () =>
            periods.map((value) => ({
                value,
                label: t(PERIOD_LABEL_KEY[value]),
            })),
        [t, periods],
    );
}

export function useChartTooltipProps(
    groupBy: GroupByOption,
    sources: Array<{ id: string; name: string; type: string }>,
) {
    const tooltipSources = useMemo(
        () =>
            sources.map((source) => ({
                id: source.id,
                name: stripFileExtension(source.name),
                type: parseEngineSourceType(source.type) ?? SourceType.CAMERA,
            })),
        [sources],
    );
    const tooltipTitleFormatter = useCallback(
        (value: string) => formatChartTooltipTitleByGroupBy(value, groupBy),
        [groupBy],
    );
    return { tooltipSources, tooltipTitleFormatter };
}
